import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  detail?: string;
}

interface ToastCtx {
  toast: (message: string, type?: ToastType, detail?: string) => void;
}

const Ctx = createContext<ToastCtx>({ toast: () => {} });

export function useToast() { return useContext(Ctx); }

const COLORS: Record<ToastType, { bg: string; border: string; icon: string; bar: string }> = {
  success: { bg: 'rgba(17,24,39,0.97)', border: 'rgba(34,197,94,0.4)',  icon: '✓', bar: '#22c55e' },
  error:   { bg: 'rgba(17,24,39,0.97)', border: 'rgba(239,68,68,0.4)',  icon: '✕', bar: '#ef4444' },
  warning: { bg: 'rgba(17,24,39,0.97)', border: 'rgba(245,158,11,0.4)', icon: '⚠', bar: '#f59e0b' },
  info:    { bg: 'rgba(17,24,39,0.97)', border: 'rgba(59,130,246,0.4)', icon: 'ℹ', bar: '#3b82f6' },
};

const TEXT: Record<ToastType, string> = {
  success: '#22c55e', error: '#ef4444', warning: '#f59e0b', info: '#3b82f6',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);

  const toast = useCallback((message: string, type: ToastType = 'success', detail?: string) => {
    const id = `t-${++counter.current}`;
    setToasts(prev => [...prev, { id, message, type, detail }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3800);
  }, []);

  return (
    <Ctx.Provider value={{ toast }}>
      {children}
      <div style={{
        position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '10px', pointerEvents: 'none',
      }}>
        {toasts.map(t => {
          const c = COLORS[t.type];
          return (
            <div key={t.id} style={{
              background: c.bg,
              border: `1px solid ${c.border}`,
              borderRadius: '12px',
              padding: '0',
              minWidth: '300px',
              maxWidth: '380px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.4)',
              pointerEvents: 'all',
              overflow: 'hidden',
              animation: 'toastIn 0.28s cubic-bezier(0.4,0,0.2,1)',
              backdropFilter: 'blur(12px)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px' }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                  background: `${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, color: TEXT[t.type],
                }}>
                  {c.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.3 }}>{t.message}</div>
                  {t.detail && <div style={{ fontSize: '11px', color: '#555555', marginTop: '3px' }}>{t.detail}</div>}
                </div>
              </div>
              {/* Progress bar */}
              <div style={{ height: '2px', background: 'rgba(255,255,255,0.06)' }}>
                <div style={{
                  height: '100%', background: c.bar, borderRadius: '2px',
                  animation: 'toastBar 3.6s linear forwards',
                }} />
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes toastIn {
          from { opacity:0; transform:translateX(40px) scale(0.96); }
          to   { opacity:1; transform:translateX(0) scale(1); }
        }
        @keyframes toastBar {
          from { width:100%; }
          to   { width:0%; }
        }
      `}</style>
    </Ctx.Provider>
  );
}
